import React, { useState } from 'react';
import { ChevronRight, ChevronLeft } from 'lucide-react';
import { ProgressBar } from './ProgressBar';
export function CheckpointNavigator() {
  const [collapsed, setCollapsed] = useState(false);
  return <aside className={`bg-white border-l border-gray-200 flex-shrink-0 flex flex-col h-full transition-all duration-300 ease-in-out relative ${collapsed ? 'w-12' : 'w-[320px]'}`} data-id="element-63">
      {/* Toggle Button */}
      <button onClick={() => setCollapsed(!collapsed)} className="absolute -left-3 top-6 z-10 h-6 w-6 rounded-full bg-[#8a38f5] text-white flex items-center justify-center shadow-md hover:bg-[#7a2de0] transition-colors" aria-label={collapsed ? 'Expand checkpoint navigator' : 'Collapse checkpoint navigator'} data-id="element-64">
        {collapsed ? <ChevronLeft className="h-3.5 w-3.5" data-id="element-65" /> : <ChevronRight className="h-3.5 w-3.5" data-id="element-66" />}
      </button>

      {/* Collapsed State - Vertical Label */}
      {collapsed && <div className="flex flex-col items-center pt-16 gap-3" data-id="element-67">
          <div className="h-2 w-2 rounded-full bg-[#8a38f5]" data-id="element-68"></div>
          <span className="text-[10px] font-bold text-gray-400 tracking-wider" style={{
        writingMode: 'vertical-rl',
        textOrientation: 'mixed'
      }} data-id="element-69">
            CHECKPOINTS
          </span>
        </div>}

      {/* Expanded Content */}
      <div className={`overflow-y-auto overflow-x-hidden transition-opacity duration-200 ${collapsed ? 'opacity-0 pointer-events-none absolute' : 'opacity-100'}`} data-id="element-70">
        <div className="p-6" data-id="element-71">
          <div className="flex items-center mb-1" data-id="element-72">
            <div className="h-2 w-2 rounded-full bg-[#8a38f5] mr-2" data-id="element-73"></div>
            <h2 className="text-sm font-bold text-[#1e1e1e]" data-id="element-74">
              Checkpoint Navigator
            </h2>
          </div>
          <p className="text-xs text-gray-500 mb-6 ml-4" data-id="element-75">
            3 checkpoints this session
          </p>

          <div className="space-y-6" data-id="element-76">
            {/* Checkpoint 1 - Active */}
            <div className="border-l-4 border-[#34a853] pl-4 py-1" data-id="element-77">
              <div className="flex items-center mb-2" data-id="element-78">
                <span className="bg-[#e9dafe] text-[#8a38f5] text-[10px] font-bold px-1.5 py-0.5 rounded mr-2" data-id="element-79">
                  CP1
                </span>
                <h3 className="text-sm font-bold text-[#1e1e1e]" data-id="element-80">
                  Slope & Y-Intercept
                </h3>
              </div>

              <div className="flex items-center justify-between text-xs text-gray-500 mb-1" data-id="element-81">
                <span data-id="element-82">2:14 messages 1-10</span>
                <span className="text-[#e8453c] font-medium" data-id="element-83">45%</span>
              </div>
              <ProgressBar percentage={45} color="#e8453c" className="mb-4" data-id="element-84" />

              <h4 className="text-xs font-medium text-gray-400 mb-3" data-id="element-85">
                Topics & Progress
              </h4>

              <div className="space-y-3 mb-4" data-id="element-86">
                <div data-id="element-87">
                  <div className="flex justify-between text-[10px] text-gray-500 mb-1" data-id="element-88">
                    <span data-id="element-89">Decimals</span>
                    <span className="text-[#34a853]" data-id="element-90">100%</span>
                  </div>
                  <ProgressBar percentage={100} color="#34a853" height="h-1" data-id="element-91" />
                </div>

                <div data-id="element-92">
                  <div className="flex justify-between text-[10px] text-gray-500 mb-1" data-id="element-93">
                    <span data-id="element-94">Mastery</span>
                    <span className="text-[#34a853]" data-id="element-95">88%</span>
                  </div>
                  <ProgressBar percentage={88} color="#34a853" height="h-1" data-id="element-96" />
                </div>

                <div data-id="element-97">
                  <div className="flex justify-between text-[10px] text-gray-500 mb-1" data-id="element-98">
                    <span data-id="element-99">Place Value</span>
                    <span className="text-[#34a853]" data-id="element-100">100%</span>
                  </div>
                  <ProgressBar percentage={100} color="#34a853" height="h-1" data-id="element-101" />
                </div>

                <div data-id="element-102">
                  <div className="flex justify-between text-[10px] text-gray-500 mb-1" data-id="element-103">
                    <span data-id="element-104">Cards</span>
                    <span className="text-[#34a853]" data-id="element-105">50%</span>
                  </div>
                  <ProgressBar percentage={50} color="#e8453c" height="h-1" data-id="element-106" />
                </div>
              </div>

              <div className="bg-[#f3f1f6] rounded-lg p-3" data-id="element-107">
                <p className="text-[10px] font-bold text-[#8a38f5] mb-1" data-id="element-108">
                  Key Insights
                </p>
                <p className="text-xs text-gray-600 leading-relaxed" data-id="element-109">
                  Student confused about what m and b represent in
                  slope-intercept form
                </p>
              </div>
            </div>

            {/* Checkpoint 2 */}
            <div className="pl-4 py-1 opacity-60" data-id="element-110">
              <div className="flex items-center mb-2" data-id="element-111">
                <span className="bg-gray-100 text-gray-500 text-[10px] font-bold px-1.5 py-0.5 rounded mr-2" data-id="element-112">
                  CP2
                </span>
                <h3 className="text-sm font-bold text-[#1e1e1e]" data-id="element-113">
                  Negative Slopes
                </h3>
              </div>

              <div className="flex items-center justify-between text-xs text-gray-500 mb-1" data-id="element-114">
                <span data-id="element-115">2:30 messages 11-19</span>
                <span className="text-[#ffce2f] font-medium" data-id="element-116">62%</span>
              </div>
              <ProgressBar percentage={62} color="#ffce2f" className="mb-4" data-id="element-117" />
            </div>
          </div>
        </div>
      </div>
    </aside>;
}